/* eslint-disable max-lines, no-console */
import Immutable, { List } from 'immutable';
import { createStructuredSelector } from 'reselect';
import qs from 'qs';
import { StatusCodes } from 'http-status-codes';

import ConfigurationService, { ConfigurationKeys } from 'services/ConfigurationService';
import LocalStorageService from 'services/LocalStorageService';
import CookieStorageService from 'services/CookieStorageService';
import getMessagingInstance from 'services/MessagingService/MessagingService';
import { SET_USER_GENDER_PREFERENCE } from 'services/MessagingService/EventNames';
import TracingService from 'services/TracingService';
import ApiService from 'services/ApiService/ApiService';
import { FILTER_CATEGORIES, TAGS_COUNT_GET_NEW } from 'enum/helpers';
import { GENDER } from 'enum/profile';
import { getGenderStr, getTranslatedGender } from 'utils/gender';
import { formatName, formatRegionName } from 'utils/helpers';
import parseCatch from 'utils/parseCatch';

import PrivateDirectoryService from '../PrivateDirectory/PrivateDirectoryService';

import { getDirectoryURL } from './DirectoryHelpers';
import {
  DIRECTORY_BANNER_URL,
  DIRECTORY_URL_COUNTS,
  FILTER_COLLECTIONS_URL,
  FILTER_COLLECTION_ITEMS_URL,
  HTML_NOTIFICATIONS_URL,
  NEW_FILTERS_URL,
  NEW_TAGS_URL,
  USER_CONSENT_URL,
} from './Api';


const BASE_URL = ConfigurationService.get(ConfigurationKeys.BASE_URL);

const MessagingService = getMessagingInstance();

const ACTIVE_FILTER_COLLECTION = 'cam4/directory/ACTIVE_FILTER_COLLECTION';
const ALL_FILTERS = 'cam4/directory/ALL_FILTERS';
const DEFAULT_ORDER = 'VIDEO_QUALITY';
const DELETE_FILTER_COLLECTION = 'cam4/directory/DELETE_FILTER_COLLECTION';
const DIRECTORY = 'cam4/studio/DIRECTORY';
const DIRECTORY_BANNER = 'cam4/directory/BANNER';
const FILTER_COLLECTIONS = 'cam4/directory/FILTER_COLLECTIONS';
const FILTER_COLLECTION_ITEMS = 'cam4/directory/FILTER_COLLECTION_ITEMS';
const FRIEND_REQUESTS = 'cam4/directory/FRIEND_REQUESTS';
const GENDER_PREFERENCE = 'cam4/studio/GENDER_PREFERENCE';
const HTML_NOTIFICATIONS = 'cam4/directory/HTML_NOTIFICATIONS';
const INITIAL_FILTERS = 'cam4/directory/INITIAL_FILTERS';
const IS_ANY_MODAL_SHOWN = 'IS_ANY_MODAL_SHOWN';
const MAKE_FILTER_STRINGS = 'cam4/directory/MAKE_FILTER_STRINGS';
const MORE_CAMS_FOR_FILTER = 'cam4/directory/MORE_CAMS_FOR_FILTER';
const MORE_TAGS = 'MORE_TAGS';
const NEW_PERFORMER_FILTER = 'cam4/directory/NEW_PERFORMER_FILTER';
const C2C_FILTER = 'cam4/directory/C2C_FILTER';
const SEG_LNG = 'cam4/directory/SEG_LNG';
const SEG_NEW = 'cam4/directory/SEG_NEW';
const SHORT_TAGS = 'cam4/studio/SHORT_TAGS';
const SHOW_CAMS_SUGGESTIONS = 'cam4/directory/SHOW_CAMS_SUGGESTIONS';
const TAGS = 'cam4/studio/TAGS';
const TAGS_COUNT = 'cam4/studio/TAGS_COUNT';
const USER_CONSENT = 'cam4/directory/USER_CONSENT';

const initialState = Immutable.fromJS({
  activeFilterCollectionId: null,
  activeFilterCollectionItems: {},
  allFilters: {
    orderBy: DEFAULT_ORDER,
    page: 1,
    resultsPerPage: 60,
  },
  directoryBanner: null,
  directoryList: {},
  filterCollections: List(),
  friendRequests: {},
  genderPreference: GENDER.FEMALE,
  html5Notifications: [],
  initialFilters: {},
  isAnyModalShown: false,
  isMoreCamsAvailable: true,
  moreTags: true,
  segLngList: {},
  segNewList: {},
  shortTagList: undefined,
  showCamsSuggestions: false,
  tagsCount: 0,
  tagsList: [],
  userConsent: {},
});

// eslint-disable-next-line complexity
export default function reducer(state = initialState, action) {
  switch (action.type) {
    case DIRECTORY:
      return state.set('directoryList', action.directoryList);
    case SEG_LNG:
      return state.set('segLngList', action.list);
    case SEG_NEW:
      return state.set('segNewList', action.list);
    case TAGS:
      return state.set('tagsList', action.tagList);
    case MORE_TAGS:
      return state.set('moreTags', action.value);
    case SHORT_TAGS:
      return state.set('shortTagList', action.shortTagList);
    case TAGS_COUNT:
      return state.set('tagsCount', action.tagsCount);
    case GENDER_PREFERENCE:
      return state.set('genderPreference', action.genderPreference);
    case MORE_CAMS_FOR_FILTER:
      return state.set('isMoreCamsAvailable', action.status);
    case ALL_FILTERS:
      return state.set('allFilters', action.filters);
    case FILTER_COLLECTIONS:
      return state.set(
        'filterCollections',
        List.isList(action.filterCollections) ? action.filterCollections : List(action.filterCollections)
      );
    case INITIAL_FILTERS:
      return state
        .set('allFilters', action.filters)
        .set('initialFilters', action.filters);
    case ACTIVE_FILTER_COLLECTION:
      return state.set('activeFilterCollectionId', action.collectionId);
    case FILTER_COLLECTION_ITEMS:
      return state.set(
        'activeFilterCollectionItems',
        Immutable.fromJS(action.collectionItems)
      );
    case DIRECTORY_BANNER:
      return state.set('directoryBanner', action.directoryBanner);
    case DELETE_FILTER_COLLECTION:
      return state.set(
        'filterCollections',
        state
          .get('filterCollections')
          .filter((collection) => collection.searchId !== action.searchId)
      );
    case SHOW_CAMS_SUGGESTIONS:
      return state.set('showCamsSuggestions', action.status);
    case NEW_PERFORMER_FILTER:
      return state.setIn(['allFilters', 'newPerformer'], action.newPerformer);
    case C2C_FILTER:
      return state.setIn(['allFilters', 'c2cAllowed'], action.c2cAllowed);
    case FRIEND_REQUESTS:
      return state.set('friendRequests', action.friendRequests);
    case USER_CONSENT:
      return state.set('userConsent', action.userConsent);
    case HTML_NOTIFICATIONS:
      return state.set('html5Notifications', action.html5Notifications);
    case IS_ANY_MODAL_SHOWN:
      return state.set('isAnyModalShown', action.payload);
    default:
      return state;
  }
}

export const NAME = 'directory';

function setIsAnyModalShown(show) {
  return {
    type: IS_ANY_MODAL_SHOWN,
    payload: show,
  };
}

function parseDirectoryList(directoryList) {
  return {
    type: DIRECTORY,
    directoryList,
  };
}

function parseDirectoryBanner(directoryBanner) {
  return {
    type: DIRECTORY_BANNER,
    directoryBanner,
  };
}

function parseHTMLNotifications(html5Notifications) {
  return {
    type: HTML_NOTIFICATIONS,
    html5Notifications,
  };
}

function parseTagsList(tagList) {
  return {
    type: TAGS,
    tagList,
  };
}

function parseShortTagsListNew(showTags) {
  const shortTagList = showTags.map((showTag) => showTag.name);

  return {
    type: SHORT_TAGS,
    shortTagList,
  };
}

function getTagsCount(tagsCount) {
  return {
    type: TAGS_COUNT,
    tagsCount,
  };
}

function setGenderPreference(genderPreference) {
  MessagingService.emit(SET_USER_GENDER_PREFERENCE, genderPreference);

  return {
    type: GENDER_PREFERENCE,
    genderPreference,
  };
}

function parseNewPerformerFilters(newPerformer) {
  return {
    type: NEW_PERFORMER_FILTER,
    newPerformer,
  };
}

function parseC2cFilters(c2cAllowed) {
  return {
    type: C2C_FILTER,
    c2cAllowed,
  };
}

function setActiveFilterCollection(collectionId) {
  return {
    type: ACTIVE_FILTER_COLLECTION,
    collectionId,
  };
}

function parseSegList(type, data) {
  const threshold = 24;
  const list = { ...data };

  if (list.users.length > threshold) {
    list.users = list.users.slice(0, threshold);
  }

  return { type, list };
}

function parseFilterCollections(filterCollections) {
  return {
    type: FILTER_COLLECTIONS,
    filterCollections,
  };
}

function setNewFilter(filters) {
  return {
    type: ALL_FILTERS,
    filters,
  };
}

function moreTagsLeft(value) {
  return {
    type: MORE_TAGS,
    value,
  };
}

const rootFilterKeys = [
  'newPerformer',
  'c2cAllowed',
  'kiiroo',
  'hd',
  'source',
  'favorites',
  'friends',
  'couple',
  'allGenders',
];
const filterKeyToUrlStringMap = {
  newPerformer: 'new',
  source: 'mobile',
  kiiroo: 'livetouch',
  allGenders: 'all',
  c2cAllowed: 'c2c',
};
const ignoredQueryFilters = ['couple', 'allGenders'];

// TODO: Remove "appliedFilters" mechanism replacing it with operations with "filters"
/**
 * Make page URL, request URL and "newFilters" object from current filters and appliedFilters.
 *
 * If "appliedFilters" is present, and "filters" has a key that "appliedFilters" includes,
 * then this filter will be enabled in "newFilters".
 *
 * All "locale" filters enables only with "appliedFilters".
 * @param {Object} filters Current filters object (ImmutableJS)
 * @param {String[]} appliedFilters Array of applied filters (from the URL)
 * @returns
 */
function makeFilterStrings(filters, appliedFilters) {
  const defaultLanguage = 'en';
  const filterObj = {};
  const urlParts = new Set();
  let newFilters = filters;
  const translatedGenders = getTranslatedGender(defaultLanguage);
  filters.forEach((value, key) => {
    if (rootFilterKeys.includes(key)) {
      if (value) {
        if (!ignoredQueryFilters.includes(key)) {
          filterObj[key] = value;
        }
        urlParts.add(filterKeyToUrlStringMap[key] || key);
      } else {
        newFilters = newFilters.delete(key);
      }
    } else if (key === 'shared') {
      value.forEach((list, key2) => {
        const filterKey = FILTER_CATEGORIES[key2];
        filterObj[filterKey] = [];
        list.forEach((filter, index) => {
          if (appliedFilters) {
            if (
              appliedFilters.includes(filter.get('urlPath')) &&
              !filter.get('active')
            ) {
              filterObj[filterKey].push(filter.get('value'));
              newFilters = newFilters.setIn([key, key2, index, 'active'], true);
              urlParts.add(filter.get('urlPath'));
            }
          }
          if (filter.get('active')) {
            filterObj[filterKey].push(filter.get('value'));
            if (key2 !== FILTER_CATEGORIES.broadcastType) {
              urlParts.add(filter.get('urlPath'));
            }
          }
        });
      });
    } else if (key === 'contextual') {
      value.forEach((genderContextual, gender) => {
        genderContextual.forEach((list, key2) => {
          const filterKey = FILTER_CATEGORIES[key2];
          filterObj[filterKey] = [];
          list.forEach((filter, index) => {
            if (appliedFilters) {
              if (
                appliedFilters.includes(filter.get('urlPath')) &&
                !filter.get('active')
              ) {
                filterObj[filterKey].push(filter.get('value'));
                newFilters = newFilters.setIn(
                  [key, gender, key2, index, 'active'],
                  true
                );
                urlParts.add(filter.get('urlPath'));
              }
            } else if (filter.get('active')) {
              filterObj[filterKey].push(filter.get('value'));
              urlParts.add(filter.get('urlPath'));
            }
          });
        });
      });
    } else if (key === 'locale') {
      value.forEach((locale, localeKey) => {
        let filterKey = FILTER_CATEGORIES[localeKey];
        if (localeKey === 'languages' && window.dsUseLanguagesABT === true) {
          filterKey = 'languageAbTest';
        }
        filterObj[filterKey] = [];
        locale.forEach((val, index) => {
          const localeName = formatName(val);
          if (appliedFilters?.includes(localeName) || val.get('active')) {
            filterObj[filterKey].push(val.get('code'));
            newFilters = newFilters.setIn(
              [key, localeKey, index, 'active'],
              true
            );
            urlParts.add(localeName);
          }
        });
      });
    } else if (key === FILTER_CATEGORIES.regionals) {
      filterObj.regional = [];
      value.forEach((region, index) => {
        const regionName = formatRegionName(region.get('name'));
        if (appliedFilters?.includes(regionName) || region.get('active')) {
          filterObj.regional.push(region.get('code').toUpperCase());
          newFilters = newFilters.setIn([key, index, 'active'], 'test');
        }
      });
    } else if (key === 'boosted') {
      filterObj.boosted = [];
      if (appliedFilters?.includes('boosted') || value.get('active')) {
        filterObj.boosted.push(true);
        newFilters = newFilters.setIn([key, 'active'], true);
      }
    } else if (key === 'recentlyWatched') {
      filterObj.username = [];
      const recentlyWatchedUsers = JSON.parse(LocalStorageService.get('cam4_recently_watched')) || [];

      if (appliedFilters?.includes('recently-watched') || value.get('active')) {
        recentlyWatchedUsers.forEach((username) => {
          filterObj.username.push(username.trim());
        });
        newFilters = newFilters.setIn([key, 'active'], true);
      }
    } else if (key === 'dailyAward') {
      filterObj.dailyAward = [];
      if (appliedFilters?.includes('cam-of-the-day') || value.get('active')) {
        filterObj.dailyAward.push(true);
        newFilters = newFilters.setIn([key, 'active'], true);
      }
    } else {
      filterObj[key] = value;
    }
  });
  const queryString = `&${qs.stringify(filterObj, { arrayFormat: 'repeat' })}`;

  const urlPartsSorted = [...urlParts]
    // Gender preference should come first in the URL
    .sort((a, b) => {
      if (translatedGenders[a]) {
        return -1;
      }
      if (translatedGenders[b]) {
        return 1;
      }

      return 0;
    })
    // Translate gender preference part
    .map((urlPart) => translatedGenders[urlPart] || urlPart);
  const urlString = urlPartsSorted.length ? `/${urlPartsSorted.join('/')}` : '';

  return {
    queryString,
    urlString,
    newFilters,
    type: MAKE_FILTER_STRINGS,
  };
}

function moreCams(status) {
  return {
    type: MORE_CAMS_FOR_FILTER,
    status,
  };
}

function showSuggestions(status) {
  return {
    type: SHOW_CAMS_SUGGESTIONS,
    status,
  };
}

/** Get gender name by filter's key */
const keyToGenderMap = {
  femaleContextualOptions: 'female',
  maleContextualOptions: 'male',
  transgenderContextualOptions: 'shemale',
};

/** Get urlPath by filter name */
const filterToUrlPathMap = {
  shemale: 'transgender',
  latino: 'hispanic',
  caucasian: 'white',
};

/** Get urlPath by filter name for hairColors */
const hairColorsFilterToUrlPathMap = {
  black: 'black-hair',
  brown: 'brown-hair',
  red: 'red-hair',
  grey: 'grey-hair',
  white: 'white-hair',
  blonde: 'blonde-hair',
  other: 'other-hair',
};

/** Get urlPath by contextual filter name */
const contextualFilterToUrlPathMap = {
  breastSizes: '{filter}-breasts',
  femaleBodyTypes: '{filter}-female-body',
  maleBodyTypes: '{filter}-male-body',
  maleTypes: '{filter}-male-type',
  penisSizes: '{filter}-penis',
  transPenisSizes: '{filter}-trans-penis',
  transBreastSizes: '{filter}-trans-breasts',
  transPenisTypes: 'trans-{filter}',
  transBodyTypes: '{filter}-trans-body',
};

/** Get urlPath by filter name for bodyHairs */
const bodyHairsFilterToUrlPathMap = { average: 'average-hair' };

/** Get urlPath by filter name for showTypes */
const showTypesFilterToUrlPathMap = {
  privateShow: 'private-show',
  groupShow: 'group-show',
};

/** Get urlPath by filter name for bodyTypes */
const bodyTypesFilterToUrlPathMap = {
  average: 'average-body',
  slimPetite: 'slim-petite',
  morethanAverage: 'more-than-average',
};

const ignoredSharedFilters = ['sources', 'broadcastTypes', 'booleanFilters'];

/**
 * Translate all the possible filters received from the backend
 * into grouped internal object
*/
function parseAllFilters(filters) {
  const parsedFilters = {
    shared: {},
    contextual: {
      male: {},
      female: {},
      shemale: {},
    },
    locale: {},
    regionals: {},
    boosted: { active: false },
    recentlyWatched: { active: false },
    dailyAward: { active: false },
    page: 1,
    orderBy: DEFAULT_ORDER,
    resultsPerPage: 60,
  };

  const filterKeys = Object.keys(filters);

  filterKeys.forEach((key) => {
    if (key.includes('Contextual')) {
      const gender = keyToGenderMap[key] || 'female';
      const contextualKeys = Object.keys(filters[key]);
      contextualKeys.forEach((cKey) => {
        const section = filters[key][cKey];
        if (Array.isArray(section)) {
          const values = section.map((filter) => {
            const urlPath =
              contextualFilterToUrlPathMap[cKey]?.replace('{filter}', filter) ||
              filter;

            return {
              value: filter,
              urlPath: urlPath.toLowerCase().replace(/_/g, '-'),
              label: filter.toLowerCase(),
              active: false,
            };
          });

          // TODO: It's so unclear that we have transBodyTypes away from maleBodyTypes and femaleBodyTypes.
          if (cKey === 'transBodyTypes') {
            parsedFilters.shared.transBodyTypes = values;
          } else {
            parsedFilters.contextual[gender][cKey] = values;
          }
        }
      });
    } else if (key === 'countries' || key === 'languages') {
      parsedFilters.locale[key] = filters[key].map((filter) => {
        return {
          ...filter,
          active: false,
        };
      });
    } else if (key === 'regionals') {
      parsedFilters.regionals = filters[key].map((filter) => {
        return {
          ...filter,
          active: false,
        };
      });
    } else if (!ignoredSharedFilters.includes(key)) {
      const section = filters[key];

      if (Array.isArray(section)) {
        const values = section
          .filter((filter) => filter.toLowerCase() !== 'unknown')
          .map((filter) => {
            // urlPath equal to filter name by default
            let urlPath = filter;
            // but for some of them we have different urlPath
            if (filterToUrlPathMap[filter]) {
              urlPath = filterToUrlPathMap[filter];
            } else if (
              key === 'hairColors' &&
              hairColorsFilterToUrlPathMap[filter]
            ) {
              urlPath = hairColorsFilterToUrlPathMap[filter];
            } else if (
              key === 'bodyHairs' &&
              bodyHairsFilterToUrlPathMap[filter]
            ) {
              urlPath = bodyHairsFilterToUrlPathMap[filter];
            } else if (
              key === 'showTypes' &&
              showTypesFilterToUrlPathMap[filter]
            ) {
              urlPath = showTypesFilterToUrlPathMap[filter];
            } else if (
              key === 'bodyTypes' &&
              bodyTypesFilterToUrlPathMap[filter]
            ) {
              urlPath = bodyTypesFilterToUrlPathMap[filter];
            }

            return {
              value: filter,
              urlPath: urlPath.toLowerCase().replace(/_/g, '-'),
              label: filter,
              active: false,
            };
          });
        parsedFilters.shared[key] = values;
      }
    }
  });

  return {
    type: INITIAL_FILTERS,
    filters: Immutable.fromJS(parsedFilters),
  };
}

function setFilterCollectionItems(collectionItems) {
  return {
    type: FILTER_COLLECTION_ITEMS,
    collectionItems,
  };
}

function removeFilterCollection(searchId) {
  return {
    type: DELETE_FILTER_COLLECTION,
    searchId,
  };
}

function parseFriendRequests(friendRequests) {
  return {
    type: FRIEND_REQUESTS,
    friendRequests,
  };
}

function parseUserContent(userConsent) {
  return {
    type: USER_CONSENT,
    userConsent,
  };
}

// API CALLS
function getDirectoryBanner(sexPreference) {
  return (dispatch) =>
    ApiService.get(encodeURI(DIRECTORY_BANNER_URL), { params: { sexPreference } })
      .then((res) => {
        if (res.status === StatusCodes.OK) {
          dispatch(parseDirectoryBanner(res.data));
        }
      })
      .catch((err) => parseCatch(err));
}

function getHTMLNotifications() {
  return (dispatch) =>
    ApiService.get(encodeURI(HTML_NOTIFICATIONS_URL))
      .then((res) => {
        if (res.status === StatusCodes.OK) {
          dispatch(parseHTMLNotifications(res.data));
        }
      })
      .catch((err) => parseCatch(err));
}

export const getDirectoryCounts = (filter, appliedFilters) => {
  const filterStrings = makeFilterStrings(filter, appliedFilters);
  const url = encodeURI(DIRECTORY_URL_COUNTS.concat(filterStrings.queryString));

  return ApiService.get(url);
};

function getSegmentLngList(gender, lng = 'en') {
  return (dispatch, getState) => {
    const { user } = getState();
    const isSfw = user.getIn(['user', 'sfw']);
    const directoryUrl = getDirectoryURL(isSfw);
    const filtersURI = `&gender=${gender}&language=${lng}&orderBy=MOST_VIEWERS`;

    return ApiService.get(encodeURI(directoryUrl.concat(filtersURI)))
      .then((res) => {
        if (res.status === StatusCodes.OK) {
          dispatch(parseSegList(SEG_LNG, res.data));
        }
      })
      .catch((err) => console.error(err));
  };
}

function getSegmentNewList(gender) {
  return (dispatch, getState) => {
    const { user } = getState();
    const isSfw = user.getIn(['user', 'sfw']);
    const directoryUrl = getDirectoryURL(isSfw);
    ApiService.get(
      encodeURI(directoryUrl.concat(`&gender=${gender}&newPerformer=true`))
    )
      .then((res) => {
        if (res.status === StatusCodes.OK) {
          dispatch(parseSegList(SEG_NEW, res.data));
        }
      })
      .catch((err) => console.error(err));
  };
}

function getShortTagsListNew(filter, appliedFilters) {
  const filterStrings = makeFilterStrings(filter, appliedFilters);
  // had to do a replace of results per page as they are set somewhere independently
  // and had to be limited to 10 while they reach here
  const URL = `${NEW_TAGS_URL}?usersPerTag=0${filterStrings.queryString}`
    .replace('resultsPerPage=90', 'resultsPerPage=30')
    .replace('resultsPerPage=60', 'resultsPerPage=30')
    .replace('&orderBy=VIDEO_QUALITY', '');

  return (dispatch) =>
    ApiService.get(encodeURI(URL))
      .then((res) => {
        dispatch(parseShortTagsListNew(res.data.showTags));
      })
      .catch((err) => parseCatch(err));
}

function applyFilter(filter, directoryList, appliedFilters) {
  return (dispatch, getState) => {
    const { user } = getState();
    const isSfw = user.getIn(['user', 'sfw']);
    const filterStrings = makeFilterStrings(filter, appliedFilters);
    let activeGender = GENDER.FEMALE;
    if (filter && filter.get('shared') && filter.get('shared').get('genders')) {
      const activeGenderList = filter
        .get('shared')
        .get('genders')
        .filter((gender) => gender.get('active'));
      activeGender = activeGenderList.size ? activeGenderList.get(0).get('value') : GENDER.FEMALE;
    } else if (user.getIn(['user', 'username']) === null) {
      if (CookieStorageService.get('cam4-CONTENT')) {
        const contentPref = CookieStorageService.get('cam4-CONTENT');
        activeGender = getGenderStr(contentPref);
      }
    }

    if (appliedFilters) {
      dispatch(setNewFilter(filterStrings.newFilters));
    } else {
      dispatch(setNewFilter(filter));
    }
    let directoryUrl = getDirectoryURL(isSfw);
    let shouldTrace = false;
    if (
      filter.get('orderBy') === 'VIDEO_QUALITY' &&
      typeof window.dsGetABTestRecSys === 'function' &&
      window.dsGetABTestRecSys()
    ) {
      // DS-854 DS-910
      directoryUrl = directoryUrl.concat(
        '&recommendationsRoomName=none',
        `&recommendationsForUser=${user.getIn(['user', 'username'])}`,
        `&recommendationEnginePayload=${window.dsGetABTestRecSys()}`
      );
      // DS-1127
      shouldTrace = true;
    }

    const filtersURI = filterStrings.queryString;
    const headers = shouldTrace ? TracingService.generateHeaders() : undefined;

    return ApiService.get(encodeURI(directoryUrl.concat(filtersURI)), { headers })
      .then((res) => {
        if (res.status === StatusCodes.OK) {
          const list = { ...res.data };
          if (!list.users.length) {
            dispatch(moreCams(false));
            dispatch(showSuggestions(true));
            dispatch(getSegmentLngList(activeGender));
          } else {
            dispatch(moreCams(true));
            dispatch(showSuggestions(false));
          }
          if (typeof directoryList !== 'undefined') {
            list.users = directoryList.users.concat(list.users);
          }
          dispatch(parseDirectoryList(list));
          dispatch(getShortTagsListNew(filter, appliedFilters));

          return filterStrings.urlString;
        }

        return console.error('API error');
      })
      .catch((err) => console.error(err));
  };
}

function applyFilterForApplyButton(filterStrings, gender, directoryList) {
  return (dispatch, getState) => {
    const { user } = getState();
    const isSfw = user.getIn(['user', 'sfw']);
    const directoryUrl = getDirectoryURL(isSfw);
    dispatch(setNewFilter(filterStrings.newFilters));

    return ApiService.get(
      encodeURI(directoryUrl.concat(filterStrings.queryString))
    )
      .then((res) => {
        if (res.status === StatusCodes.OK) {
          const list = { ...res.data };
          if (!list.users.length) {
            dispatch(moreCams(false));
            dispatch(showSuggestions(true));
            dispatch(getSegmentLngList(gender));
          } else {
            dispatch(moreCams(true));
            dispatch(showSuggestions(false));
          }
          if (typeof directoryList !== 'undefined') {
            list.users = directoryList.users.concat(list.users);
          }
          dispatch(parseDirectoryList(list));

          return filterStrings.urlString;
        }

        return console.error('API error');
      })
      .catch((err) => console.error(err));
  };
}

function getTagsListNew(category, page, limit, list) {
  const params = {
    page,
    resultsPerPage: limit,
    orderBy: 'MOST_VIEWERS',
    usersPerTag: 8,
    ...PrivateDirectoryService._getGenderQuery(category),
  };

  const URL = `${NEW_TAGS_URL}?${qs.stringify(params, { arrayFormat: 'repeat' })}`;

  return (dispatch) =>
    ApiService.get(URL)
      .then((res) => {
        if (typeof list !== typeof undefined) {
          if (res.data.length === TAGS_COUNT_GET_NEW) {
            dispatch(parseTagsList(res.data.showTags));
            dispatch(getTagsCount(res.data.totalCount));
          } else {
            if (res.data.length !== 0) {
              dispatch(parseTagsList(res.data.showTags));
              dispatch(getTagsCount(res.data.totalCount));
            }
            dispatch(moreTagsLeft(false));
          }
        } else {
          dispatch(parseTagsList(res.data.showTags));
          dispatch(getTagsCount(res.data.totalCount));
        }
      })
      .catch((err) => {
        console.error(err);
      });
}

function getFilters() {
  return (dispatch) =>
    ApiService.get(NEW_FILTERS_URL)
      .then((res) => {
        if (res.status === StatusCodes.OK) {
          dispatch(parseAllFilters(res.data));
        }
      })
      .catch((err) => console.error(err));
}

function getFilterCollections(userId) {
  const url = ApiService.substitute(FILTER_COLLECTIONS_URL, { userId });

  return (dispatch) =>
    ApiService.get(encodeURI(url))
      .then((res) => {
        if (res.status === StatusCodes.OK) {
          dispatch(parseFilterCollections(res.data));
        }
      })
      .catch((err) => console.error(err));
}

function setFilterCollection(filters, name, userId, filterCollections) {
  const filterStrings = makeFilterStrings(filters);
  const data = `${filterStrings.queryString.substring(1)}&name_search=${name}`;
  const url = ApiService.substitute(FILTER_COLLECTIONS_URL, { userId });

  return (dispatch) =>
    ApiService.post(encodeURI(url), {
      data,
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
    })
      .then((res) => {
        if (res.status === StatusCodes.OK) {
          const newCollection = {
            locked: false,
            queryJson: {},
            searchId: res.data.search_id,
            searchName: name,
            userId,
          };
          dispatch(
            parseFilterCollections(
              filterCollections.insert(
                filterCollections.size + 1,
                newCollection
              )
            )
          );
          dispatch(setActiveFilterCollection(newCollection.searchId));

          return Promise.resolve(res.data);
        }

        return undefined;
      })
      .catch((err) => console.error(err));
}

function getFilterCollectionItems(userId, searchId) {
  const url = ApiService.substitute(FILTER_COLLECTION_ITEMS_URL, {
    userId,
    searchId,
  });

  return (dispatch) =>
    ApiService.get(encodeURI(url))
      .then((res) => {
        if (res.status === StatusCodes.OK) {
          dispatch(setFilterCollectionItems(res.data));

          return res.data;
        }

        return undefined;
      })
      .catch((error) => console.error(error));
}

function deleteFilterCollection(userId, searchId) {
  const url = ApiService.substitute(FILTER_COLLECTION_ITEMS_URL, {
    userId,
    searchId,
  });

  return (dispatch) =>
    ApiService.delete(encodeURI(url))
      .then((res) => {
        if (res.status === StatusCodes.OK) {
          dispatch(removeFilterCollection(searchId));
        }
      })
      .catch((error) => console.error(error));
}

function getFriendshipRequests(limit, offset) {
  const url = `${BASE_URL}/friendRequests`;
  const params = {
    limit,
    offset,
  };

  return (dispatch) =>
    ApiService.get(url, { params })
      .then((res) => {
        if (res.status === StatusCodes.OK) {
          const response = {
            received: res.data.response.received.map((r) => {
              return {
                name: r,
                isAccepted: false,
                isDeclined: false,
              };
            }),
            totalCount: res.data.response.totalCount,
          };
          dispatch(parseFriendRequests(response));
        }
      })
      .catch((error) => parseCatch(error));
}

function updateFriendshipRequests(friendUsername, operation, friendRequests) {
  return (dispatch) => {
    switch (operation) {
      case 'accept':
        friendRequests.received = friendRequests.received.map((r) => {
          if (r.name === friendUsername) {
            r.isAccepted = true;
          }

          return r;
        });
        dispatch(
          parseFriendRequests({
            ...friendRequests,
            totalCount:
              friendRequests.totalCount > 1 ? friendRequests.totalCount - 1 : friendRequests.totalCount,
          })
        );

        return true;
      case 'ignore':
        friendRequests.received = friendRequests.received.map((r) => {
          if (r.name === friendUsername) {
            r.isDeclined = true;
          }

          return r;
        });
        dispatch(
          parseFriendRequests({
            ...friendRequests,
            totalCount:
              friendRequests.totalCount > 1 ? friendRequests.totalCount - 1 : friendRequests.totalCount,
          })
        );

        return true;
      case 'acceptAll':
        friendRequests.received.forEach((r) => {
          r.isAccepted = true;
        });
        dispatch(parseFriendRequests({ ...friendRequests }));

        return true;
      case 'ignoreAll':
        friendRequests.received.forEach((r) => {
          r.isDeclined = true;
        });
        dispatch(parseFriendRequests({ ...friendRequests }));

        return true;
      default:
        return false;
    }
  };
}

function getUserConsent(username, consentType) {
  const url = ApiService.substitute(USER_CONSENT_URL, {
    username,
    consentType,
  });

  return (dispatch) =>
    ApiService.get(url)
      .then((res) => {
        dispatch(parseUserContent(res.data));
      })
      .catch((error) => parseCatch(error));
}

function changeActiveFilterCollection(collectionId) {
  return (dispatch) => dispatch(setActiveFilterCollection(collectionId));
}

function resetFilterCollectionItems() {
  return (dispatch) => dispatch(setFilterCollectionItems({}));
}

function setNewPerformerFilter(newFilter) {
  return (dispatch) => dispatch(parseNewPerformerFilters(newFilter));
}

function setC2cFilter(newFilter) {
  return (dispatch) => dispatch(parseC2cFilters(newFilter));
}

export const selector = createStructuredSelector({
  directoryList: (state) => state[NAME].get('directoryList'),
  tagsList: (state) => state[NAME].get('tagsList'),
  moreTags: (state) => state[NAME].get('moreTags'),
  shortTagList: (state) => state[NAME].get('shortTagList'),
  tagsCount: (state) => state[NAME].get('tagsCount'),
  genderPreference: (state) => state[NAME].get('genderPreference'),
  segLngList: (state) => state[NAME].get('segLngList'),
  segNewList: (state) => state[NAME].get('segNewList'),
  appliedFilters: (state) => state[NAME].get('appliedFilters'),
  isMoreCamsAvailable: (state) => state[NAME].get('isMoreCamsAvailable'),
  allFilters: (state) => state[NAME].get('allFilters'),
  initialFilters: (state) => state[NAME].get('initialFilters'),
  filterCollections: (state) => state[NAME].get('filterCollections'),
  activeFilterCollectionId: (state) =>
    state[NAME].get('activeFilterCollectionId'),
  activeFilterCollectionItems: (state) =>
    state[NAME].get('activeFilterCollectionItems'),
  directoryBanner: (state) => state[NAME].get('directoryBanner'),
  showCamsSuggestions: (state) => state[NAME].get('showCamsSuggestions'),
  newPerformer: (state) => state[NAME].getIn(['allFilters', 'newPerformer']),
  c2cAllowed: (state) => state[NAME].getIn(['allFilters', 'c2cAllowed']),
  kiiroo: (state) => state[NAME].getIn(['allFilters', 'kiiroo']),
  hd: (state) => state[NAME].getIn(['allFilters', 'hd']),
  source: (state) => state[NAME].getIn(['allFilters', 'source']),
  friendRequests: (state) => state[NAME].get('friendRequests'),
  userConsent: (state) => state[NAME].get('userConsent'),
  html5Notifications: (state) => state[NAME].get('html5Notifications'),
  isAnyModalShown: (state) => state[NAME].get('isAnyModalShown'),
});

export const actionCreators = {
  getSegmentLngList,
  getTagsListNew,
  setGenderPreference,
  getSegmentNewList,
  applyFilter,
  getFilters,
  getFilterCollections,
  getFilterCollectionItems,
  resetFilterCollectionItems,
  changeActiveFilterCollection,
  getDirectoryBanner,
  setFilterCollection,
  deleteFilterCollection,
  setNewPerformerFilter,
  setC2cFilter,
  getFriendshipRequests,
  updateFriendshipRequests,
  getUserConsent,
  getHTMLNotifications,
  makeFilterStrings,
  applyFilterForApplyButton,
  parseAllFilters,
  setIsAnyModalShown,
};
